import * as React from 'react'
import { Search, X } from 'lucide-react'
import { Input, type InputProps } from './Input'
import { cn } from '@/lib/cn'

/**
 * SearchInput — the search affordance, once.
 *
 * Eight places hand-rolled "a magnifier positioned over an input", and no two agreed:
 * `py-2` vs `py-1.5`, `rounded-md` vs `rounded-lg`, a full `border border-border` vs
 * `border-b-2` vs none, `text-sm` vs `text-xs` vs `text-body-sm`, icon `size={13}` vs
 * `14` vs `16`, `left-2.5` vs `left-3`. Every one of those is a decision the design
 * system had already made for `Input`; the markup around it is what drifted.
 *
 * Two shapes, because there genuinely are two:
 *
 *  - `boxed` (default) — the input draws its own surface and the icon sits on top of it.
 *    This is a form control on a page: filter bars, listing searches, palettes-in-a-panel.
 *
 *  - `flush` — the input is transparent and the PARENT draws the box. This is the command
 *    palette and the menu header, where the search field IS the container's top row and a
 *    second nested border would be wrong. It was not worth forcing those two onto `boxed`
 *    and then fighting the chrome back off with overrides.
 *
 * `type="search"`, deliberately — it is what gives the field the accessible `searchbox` role
 * (and Escape-to-clear). It also used to bring WebKit's own ✕ along with it, in WebKit's grey at
 * WebKit's size, which is why FilterBar's box had a clear button no other search box had. That
 * was a missing reset, not a reason to downgrade to `type="text"`: index.css now clears
 * `::-webkit-search-cancel-button`, and `clearable` draws ours in the system's own colours.
 */
export interface SearchInputProps extends Omit<InputProps, 'type'> {
  /** `boxed` draws its own surface; `flush` is transparent inside a parent that draws one. */
  variant?: 'boxed' | 'flush'
  /** Render a clear button while there is a value. Needs `onValueChange` to do anything. */
  clearable?: boolean
  /** Called with the new string — sugar over `onChange(e.target.value)`; also used by clear. */
  onValueChange?: (value: string) => void
  /** Classes for the wrapper, not the field (the field takes `className`). */
  wrapperClassName?: string
}

export const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
  function SearchInput(
    {
      variant = 'boxed',
      clearable = false,
      onValueChange,
      onChange,
      wrapperClassName,
      className,
      placeholder = 'Search…',
      value,
      // Pulled out of the rest: `flush` renders a bare <input>, which has no `size` variant to
      // receive — and no surface to size either, since the parent draws the box.
      size,
      ...props
    },
    ref,
  ) {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(e)
      onValueChange?.(e.target.value)
    }

    const showClear = clearable && typeof value === 'string' && value.length > 0

    if (variant === 'flush') {
      return (
        <div className={cn('flex items-center gap-2.5 min-w-0 w-full', wrapperClassName)}>
          <Search size={14} aria-hidden className="shrink-0 text-text-subtle" />
          <input
            ref={ref}
            type="search"
            value={value}
            onChange={handleChange}
            placeholder={placeholder}
            className={cn(
              'flex-1 min-w-0 bg-transparent outline-none',
              'text-body-md text-text placeholder:text-text-subtle',
              className,
            )}
            {...props}
          />
          {showClear && (
            <button
              type="button"
              onClick={() => onValueChange?.('')}
              aria-label="Clear search"
              className="shrink-0 text-text-subtle hover:text-text transition-colors"
            >
              <X size={14} />
            </button>
          )}
        </div>
      )
    }

    return (
      <div className={cn('relative w-full', wrapperClassName)}>
        <Search
          size={14}
          aria-hidden
          className="absolute left-3 top-1/2 -translate-y-1/2 text-text-subtle pointer-events-none"
        />
        <Input
          ref={ref}
          type="search"
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          size={size}
          className={cn('pl-9', showClear && 'pr-9', className)}
          {...props}
        />
        {showClear && (
          <button
            type="button"
            onClick={() => onValueChange?.('')}
            aria-label="Clear search"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-text-subtle hover:text-text transition-colors"
          >
            <X size={14} />
          </button>
        )}
      </div>
    )
  },
)
